import { ContainerPadding, CTA, Spinner, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { useState } from 'react';
import { EXTERNAL_LINK, FIELD_NAME, FORM_MESSAGE, FORM_SUBMIT_STATUS } from '../../constants/form-constants';
import { STYLED_CTA } from '../../constants/styles-constants';
import { useApp } from '../../contexts/app-context';
import { ndpsEntity } from '../../entities/ndps-entity';
import { formService } from '../../form-service';
import { isComerciales } from '../../helpers/carline-helpers';
import { formDataBuilder, formDataFilter } from '../../helpers/form-helpers';
import { sendForm } from '../../service/api/send-form';
import { externalContentManager } from '../../service/external-content';
import { useTrackingManager } from '../../tracking/use-tracking-manager';
import { ErrorSection } from '../screen/error';
import { Section } from '../screen/section';

interface Props {
	formData: () => Promise<any> | Record<string, any>;
	tabAccess?: boolean;
	showInfoRequiredFields?: boolean;
	alwaysSuccess?: boolean;
	title?: string;
	errorMessage?: string;
}

export function EnvioSection(props: Props) {
	const {
		formData,
		tabAccess = true,
		showInfoRequiredFields = false,
		alwaysSuccess = false,
		title,
		errorMessage,
	} = props;
	const tabIndex = tabAccess ? 0 : -1;
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<boolean>();
	const [errorLog, setErrorLog] = useState<string>();
	const [invalid, setInvalid] = useState<boolean>();
	const trackingManager = useTrackingManager();
	const { setAppSuccess } = useApp();

	const handleClick = async (e: React.SyntheticEvent) => {
		e.preventDefault();
		setSending(true);
		setError(false);
		setInvalid(false);

		const formDataResult = await formData();
		const isValid = formService.isFormValid();

		if (!isValid) {
			formService.setFormSubmitStatus(FORM_SUBMIT_STATUS.INVALID);
			setSending(false);
			setInvalid(true);
			return;
		}

		if (!formDataResult) {
			setSending(false);
			setError(true);
			return;
		}

		const dataToSend = formDataFilter({
			...ndpsEntity.commonData,
			...externalContentManager.dpsFields,
			...formDataResult,
		});

		formService.setFormSubmitStatus(FORM_SUBMIT_STATUS.SENDING);
		const { success, response } = await sendForm(formDataBuilder(dataToSend));
		const overrideSuccess = success || alwaysSuccess;
		formService.setFormSubmitStatus(overrideSuccess ? FORM_SUBMIT_STATUS.SUCCESS : FORM_SUBMIT_STATUS.ERROR);

		if (overrideSuccess) {
			setAppSuccess(true);
			const fkSystem = (response as any)?.sent?.headers?.FK_SYSTEM;
			trackingManager.trackFormSubmissionSuccessMessageLoad(
				{ contentId: 'Envio correcto' },
				{
					LeadID: fkSystem,
					MarketingConsent: [
						{
							comunicaciones:
								formService.getFieldValue(FIELD_NAME.RGPD_COMUNICACIONES_COMERCIALES) == 'PERMITE' ? true : false,
							perfilado: formService.getFieldValue(FIELD_NAME.RGPD_PERFILADO) == 'PERMITE' ? true : false,
						},
					],
				},
			);
		} else {
			setErrorLog(JSON.stringify(response?.content));

			trackingManager.trackFormErrorMessageLoad({ message: FORM_MESSAGE.GENERIC_ERROR }, undefined);
		}

		setSending(false);
		setError(!overrideSuccess);
	};

	return (
		<>
			<Section paddingBottom={ContainerPadding.static200}>
				{title && (
					<Text bold appearance={TokenTextAppearance.copy200}>
						{title}
					</Text>
				)}
				<Text appearance={TokenTextAppearance.copy200}>
					Enviando este formulario declaras estar informado de la{' '}
					<CTA
						tag="a"
						href={isComerciales() ? EXTERNAL_LINK.POLITICA_PRIVACIDAD_COMERCIALES : EXTERNAL_LINK.POLITICA_PRIVACIDAD}
						target="_blank"
						emphasis="tertiary"
						tabIndex={tabIndex}
					>
						política de privacidad
					</CTA>
					.
				</Text>
				{showInfoRequiredFields && <Text appearance={TokenTextAppearance.copy200}>Campos obligatorios*</Text>}

				<div className="cta primary" style={{ width: STYLED_CTA.width }}>
					<CTA
						tag="button"
						emphasis="primary"
						onClick={handleClick}
						type="button"
						stretchContent
						disabled={sending}
						icon={sending ? <Spinner variant="default" /> : undefined}
						tabIndex={tabIndex}
					>
						{sending ? 'Enviando...' : 'Enviar'}
					</CTA>
				</div>
			</Section>
			{invalid ? <ErrorSection align="left">{FORM_MESSAGE.INVALID_FIELDS}</ErrorSection> : undefined}
			{error ? (
				<ErrorSection align="left" log={errorLog}>
					{errorMessage || FORM_MESSAGE.GENERIC_ERROR}
				</ErrorSection>
			) : undefined}
		</>
	);
}
